import { useMemo, useState } from "react";
import { Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  isoLocalDate,
  monthBounds,
  yearBounds,
} from "@/lib/format";

/**
 * Selettore periodo unificato per l'intera app.
 *
 * Modalità:
 *  - Preset rapidi: Oggi, Questo mese, Mese scorso, Questo anno,
 *    Anno scorso, Tutto.
 *  - Selezione Anno + Mese (12 mesi in griglia 4x3). Auto-imposta
 *    first/last day del mese scelto (gestisce 28/29/30/31 e anni
 *    bisestili).
 *  - Click "Tutto" -> from="" to="" (nessun filtro periodo).
 *
 * Props:
 *  - onChange(from, to): stringhe ISO "YYYY-MM-DD" o "" per "tutto".
 *  - value: {from, to} — usato per evidenziare il preset attivo (opz).
 *  - minYear / maxYear: range dell'Anno dropdown (default 2020-annoCorrente+2).
 *  - align: allineamento popover (default "start").
 *  - testId: prefix per data-testid.
 */
export function PeriodPicker({
  onChange,
  value,
  minYear,
  maxYear,
  align = "start",
  testId = "period-picker",
}) {
  const [open, setOpen] = useState(false);
  const nowY = new Date().getFullYear();
  const fromY = minYear ?? 2020;
  const toY = maxYear ?? nowY + 2;

  const [year, setYear] = useState(() => {
    // Se c'è un value corrente, inizializza sull'anno del from.
    if (value?.from) {
      const parsed = parseInt(value.from.slice(0, 4), 10);
      if (Number.isFinite(parsed)) return parsed;
    }
    return nowY;
  });

  const years = useMemo(() => {
    const arr = [];
    for (let y = toY; y >= fromY; y--) arr.push(y);
    return arr;
  }, [fromY, toY]);

  const months = [
    "Gen", "Feb", "Mar", "Apr",
    "Mag", "Giu", "Lug", "Ago",
    "Set", "Ott", "Nov", "Dic",
  ];

  const emit = (from, to) => {
    onChange?.(from, to);
    setOpen(false);
  };

  // ---- Handlers preset ----
  const applyToday = () => {
    const iso = isoLocalDate(new Date());
    emit(iso, iso);
  };
  const applyThisMonth = () => {
    const [f, t] = monthBounds(new Date());
    emit(f, t);
  };
  const applyLastMonth = () => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    const [f, t] = monthBounds(d);
    emit(f, t);
  };
  const applyThisYear = () => {
    const [f, t] = yearBounds(new Date());
    emit(f, t);
  };
  const applyLastYear = () => {
    const [f, t] = yearBounds(new Date(new Date().getFullYear() - 1, 0, 1));
    emit(f, t);
  };
  const applyAll = () => emit("", "");

  const applyMonth = (monthIdx) => {
    const [f, t] = monthBounds(new Date(year, monthIdx, 1));
    emit(f, t);
  };

  // ---- Determina il mese attivo (per evidenziazione) ----
  const activeMonth = useMemo(() => {
    if (!value?.from || !value?.to) return null;
    const y = parseInt(value.from.slice(0, 4), 10);
    const m = parseInt(value.from.slice(5, 7), 10) - 1;
    if (y !== year) return null;
    // Verifica che from = primo del mese e to = ultimo del mese
    const [expF, expT] = monthBounds(new Date(y, m, 1));
    if (value.from === expF && value.to === expT) return m;
    return null;
  }, [value, year]);

  // ---- Etichetta breve del range corrente ----
  const currentLabel = useMemo(() => {
    if (!value?.from && !value?.to) return "Tutto";
    if (value?.from && value?.to && value.from === value.to) {
      // giorno singolo
      return value.from.split("-").reverse().join("/");
    }
    return "Periodo";
  }, [value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid={`${testId}-trigger`}
          className="h-8 gap-1.5 px-2.5 text-xs"
          title="Selettore rapido periodo"
        >
          <Calendar size={14} />
          <span>{currentLabel === "Periodo" ? "Periodo" : "Periodo"}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        className="w-[320px] p-3"
        data-testid={`${testId}-content`}
      >
        {/* Preset rapidi */}
        <div className="mb-3">
          <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-stone-500">
            Preset rapidi
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={applyToday}
              className="h-7 px-1 text-[11px]"
              data-testid={`${testId}-today`}
            >
              Oggi
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={applyThisMonth}
              className="h-7 px-1 text-[11px]"
              data-testid={`${testId}-this-month`}
            >
              Questo mese
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={applyLastMonth}
              className="h-7 px-1 text-[11px]"
              data-testid={`${testId}-last-month`}
            >
              Mese scorso
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={applyThisYear}
              className="h-7 px-1 text-[11px]"
              data-testid={`${testId}-this-year`}
            >
              Questo anno
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={applyLastYear}
              className="h-7 px-1 text-[11px]"
              data-testid={`${testId}-last-year`}
            >
              Anno scorso
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={applyAll}
              className="h-7 px-1 text-[11px]"
              data-testid={`${testId}-all`}
            >
              Tutto
            </Button>
          </div>
        </div>

        <div className="my-2 h-px bg-stone-200" />

        {/* Anno + Mese */}
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-stone-500">
              Seleziona mese
            </div>
            <Select
              value={String(year)}
              onValueChange={(v) => setYear(parseInt(v, 10))}
            >
              <SelectTrigger
                data-testid={`${testId}-year`}
                className="h-7 w-[90px] text-xs"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((y) => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-4 gap-1">
            {months.map((label, idx) => (
              <Button
                key={label}
                type="button"
                variant={activeMonth === idx ? "default" : "outline"}
                size="sm"
                onClick={() => applyMonth(idx)}
                data-testid={`${testId}-month-${idx + 1}`}
                className="h-7 px-1 text-[11px]"
              >
                {label}
              </Button>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
